import React from "react";
import { PRIVACY_LAST_UPDATED, PRIVACY_VERSION } from "../lib/legal";

const sections = [
  {
    title: "1. Who we are",
    clauses: [
      {
        heading: "1.1",
        text: "InvoiceU provides tools to create, send, and manage invoices. If you are located in the UK or EU, we are the controller for your personal data that we collect through the App. You remain the controller for any personal data you upload about your own customers.",
      },
    ],
  },
  {
    title: "2. What we collect",
    clauses: [
      {
        heading: "2.1",
        text: "We collect the following when you use the App:",
        bullets: [
          "Account & authentication: email address, password (handled by Supabase Auth), Apple/Google sign-in identifiers, session tokens.",
          "Business profile: company name, address, city, post code, payee name, sort code, account number, and optional company logo you upload.",
          "Invoices & jobs: invoice numbers and dates, customer names and contact details, line items, job templates, payment status, notes/terms, uploaded logos referenced in invoices.",
          "Support & feedback: feature requests and support messages you submit.",
          "Device/usage: device type, OS version, app version, and basic diagnostics needed to operate and secure the service. We do not run third-party analytics SDKs.",
          "Payment & subscription: plan selection and subscription status stored in your Supabase profile. In-app card present payments (Stripe Terminal) are not yet active; if enabled later, card data is handled by Stripe, not stored by us.",
        ],
      },
    ],
  },
  {
    title: "3. How we use data",
    clauses: [
      {
        heading: "3.1",
        text: "We use your data to:",
        bullets: [
          "Provide and secure the App, including authentication and session management.",
          "Create, store, sync, and display invoices, jobs, customers, and your business profile.",
          "Generate PDFs and allow sharing/printing through your device OS.",
          "Handle account management (password resets, email confirmation, plan changes).",
          "Respond to support/feature requests.",
          "Improve reliability, including limited diagnostics and offline caching.",
        ],
      },
    ],
  },
  {
    title: "4. Legal bases (EEA/UK users)",
    clauses: [
      {
        heading: "4.1",
        text: "For users in the EEA/UK, we rely on:",
        bullets: [
          "Contract: to deliver the App features you request.",
          "Legitimate interests: to secure the service, prevent abuse, and improve reliability.",
          "Consent: where required for optional features (e.g., photo library access for logos).",
        ],
      },
    ],
  },
  {
    title: "5. Sharing and disclosures",
    clauses: [
      {
        heading: "5.1",
        text: "We share data only with service providers needed to run the App:",
        bullets: [
          "Supabase: authentication, database, storage, and edge functions (e.g., email checks, account deletion, logo uploads, feature requests). Data is stored in our Supabase project (yhuvzlvudjnbmlwphvsf.supabase.co) in the region configured for that project.",
          "Identity providers: Apple and Google when you choose those sign-in options.",
          "Email delivery: Supabase may send transactional emails (sign-up confirmation, password reset).",
          "Payments (potential future use): Stripe Terminal for in-person payments; card data is processed by Stripe and not stored in InvoiceU.",
          "OS-level sharing/printing: when you share or print PDFs, data is handed to your device OS or the destination you choose.",
          "Compliance and safety: if required by law, to protect rights and safety, or to respond to lawful requests.",
        ],
        note: "We do not sell personal data.",
      },
    ],
  },
  {
    title: "6. Storage, security, and offline data",
    clauses: [
      {
        heading: "6.1",
        text: "Where and how we store information:",
        bullets: [
          "Cloud storage: Primary data (accounts, profiles, invoices, customers, jobs, subscription status, uploaded logos) is stored in Supabase Postgres/Storage.",
          "Local storage: The App caches data for offline access using encrypted storage (Expo SecureStore + AES-encrypted AsyncStorage). Some cached data may persist on your device until you sign out, clear app data, or delete your account.",
          "Access controls: Sessions are stored securely; passwords are never stored in plaintext.",
          "No absolute security: We use reasonable safeguards but cannot guarantee absolute security of transmissions or storage.",
        ],
      },
    ],
  },
  {
    title: "7. Data retention",
    clauses: [
      {
        heading: "7.1",
        text: "We keep your data while your account is active. You can delete your account in Settings (\"Delete account\"), which triggers removal of your profile, invoices, customers, and cached data we control. Cached data on your device is cleared when you sign out or delete the app data; if you need assistance, contact us.",
      },
    ],
  },
  {
    title: "8. Your choices and rights",
    clauses: [
      {
        heading: "8.1",
        text: "You can:",
        bullets: [
          "Access, update, delete: Manage most data in the App. Delete your account from Settings or contact us.",
          "Portability: Export invoices as PDFs from within the App.",
          "Communications: We send only service/transactional emails (e.g., confirmations, resets).",
          "Permissions: You can revoke photo/media permissions in your device settings; this may limit logo uploads.",
          "EEA/UK rights: You may have rights to access, correct, delete, restrict, or object to processing. Contact us to exercise these.",
        ],
      },
    ],
  },
  {
    title: "9. Children",
    clauses: [
      {
        heading: "9.1",
        text: "The App is not directed to children under 16. Do not use the App if you are under 16.",
      },
    ],
  },
  {
    title: "10. Changes",
    clauses: [
      {
        heading: "10.1",
        text: "We may update this Policy from time to time. If changes are material, we will notify you in the App or by email where appropriate. Continued use after changes means you accept the updated Policy.",
      },
    ],
  },
  {
    title: "11. Contact",
    clauses: [
      {
        heading: "11.1",
        text: "If you have questions about this Policy or how we handle data, contact us at:",
        bullets: [
          "Email: contact@invoiceu.co.uk",
          "Website: https://invoiceu.co.uk",
        ],
      },
    ],
  },
];

const intro =
  'This Privacy Policy explains how InvoiceU ("we", "us", "our") collects, uses, and protects information when you use the InvoiceU mobile application (the "App"). By using the App, you agree to this Policy.';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <header className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Privacy Policy
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              InvoiceU Privacy Policy
            </h1>
            <p className="text-xs text-slate-500">
              Version {PRIVACY_VERSION} · Last updated {PRIVACY_LAST_UPDATED}
            </p>
          </div>
          <a
            href="/"
            className="self-start text-xs font-semibold text-slate-700 underline hover:text-slate-900"
          >
            Back to app
          </a>
        </header>

        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
          {intro}
        </div>

        <div className="grid gap-4">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
            >
              <h2 className="text-sm font-semibold text-slate-900">
                {section.title}
              </h2>
              <div className="mt-2 space-y-3">
                {section.clauses.map((clause) => (
                  <div
                    key={`${section.title}-${clause.heading}`}
                    className="text-sm leading-relaxed text-slate-700"
                  >
                    <p>
                      <span className="font-semibold text-slate-900">
                        {clause.heading}{" "}
                      </span>
                      {clause.text}
                    </p>
                    {clause.bullets && (
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        {clause.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                    {clause.note && (
                      <p className="mt-2 text-sm leading-relaxed text-slate-700">
                        {clause.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
